<template>
  <UikitDialog
    :model-value="modelValue"
    full-height
    :with-mobile-menu="!isDesktop"
    :no-shadow="!isDesktop"
    :hide-backdrop="!isDesktop"
    close-others-on-mobile
    no-padding
    dark
    position="right"
    @handle-close="onCloseClick"
    @update:model-value="emits('update:modelValue', $event)"
  >
    <div class="wrapper">
      <div
        v-if="!ownerMode"
        class="community-header-image"
        :class="[isMale ? 'male' : 'female']"
      />

      <div class="content">
        <div
          v-if="user && ownerMode"
          class="flex flex-col items-center mb-6"
        >
          <UiAvatar
            :user="user"
            size="xl"
            class="avatar"
            style="margin-right: 0;"
          />

          <account-o-community-badge
            v-if="user.communityBadge"
            :model-value="user.communityBadge"
            dark-mode
            class="badge badge--top-margined"
          />

          <div class="counts mt-4">
            {{ purchaseCount }} {{ pluralize(purchaseCount, ['покупка', 'покупки', 'покупок']) }} • {{ saleCount }} {{ pluralize(saleCount, ['продажа', 'продажи', 'продаж']) }}
          </div>

          <div class="counts-duration mb-4">
            за последние полгода
          </div>

          <div class="text text-center">
            Вы состоите в O!Community – сообществе влюбленных в моду и стиль
          </div>
        </div>

        <div
          v-if="!ownerMode"
          class="mb-8"
        >
          <h1 class="main-title playfair mb-2">
            O!Community
          </h1>

          <p class="main-subtitle mb-8">
            Сообщество влюбленных<br>в моду
          </p>

          <div
            v-if="user"
            class="flex mb-5"
          >
            <UiAvatar
              :user="user"
              size="ml"
              class="avatar non-owner-avatar"
            />

            <div style="min-width: 0;">
              <div class="nickname mb-2">
                @{{ user.nickname }}
              </div>

              <account-o-community-badge
                v-if="user.communityBadge"
                :model-value="user.communityBadge"
                dark-mode
                class="badge"
              />
            </div>
          </div>

          <p
            v-if="statusRequirementsText"
            class="text"
            v-html="statusRequirementsText"
          />
        </div>

        <div
          v-if="ownerMode"
          class="mb-8"
        >
          <h3 class="title title--top-separated mb-2">
            Ваши привилегии
          </h3>

          <p class="text">
            Персональные промокоды, партнерские мероприятия и другие привилегии, полный список в приложении OSKELLY
          </p>
        </div>

        <div class="mb-8">
          <h3 class="title title--top-separated mb-2">
            О сообществе
          </h3>

          <p class="text">
            O!Community – мир, в котором становится возможным объединять страсть к моде с привилегиями уникального сообщества.
            Покупайте, продавайте, вдохновляйтесь и вдохновляйте — каждый шаг будет приближать вас к новым высотам и эксклюзивным бонусам, доступным только членам O!Community.
          </p>
        </div>

        <div>
          <h3
            v-if="ownerMode"
            class="title title--top-separated mb-2"
          >
            Скачать приложение OSKELLY
          </h3>
          <h3
            v-else
            class="title title--top-separated mb-2"
          >
            Больше информации о сообществе в приложении OSKELLY
          </h3>

          <div>
            <UIButton
              v-if="isMobile && isAppleDevice"
              :href="apps.appStoreRU"
              target="_blank"
              type="black-border"
              size="s"
              class="mt-6"
              style="width: 100%;"
            >
              <UiIcon
                name="app-store-eng"
                style="width: 110px; height: 30px;"
              />
            </UIButton>

            <UIButton
              v-else-if="isMobile && isAndroidDevice"
              :href="apps.playMarket"
              target="_blank"
              type="black-border"
              size="s"
              class="mt-6"
              style="width: 100%;"
            >
              <UiIcon
                name="google-play-eng"
                style="width: 115px; height: 25px;"
              />
            </UIButton>

            <img
              v-else
              src="~assets/img/o-community/qr-code.svg"
              alt="QR code"
            >
          </div>
        </div>
      </div>
    </div>
  </UikitDialog>
</template>

<script setup lang="ts">
import { AnalyticsEvents } from '~/types/analytics';
import { Community } from '~/restAPI/Community';
import {
  CommunityInfos,
  CommunityStatusRequirement,
  PublicProfileDTO,
} from '~/restAPI/data-contracts';
import { pluralize } from '~/utils/extra';
import { useApi } from '~/restAPI';
import { useDevices } from '~/composables/useDevices';

import UIButton from '~/components/ui/UIButton.vue';

const props = defineProps<{
  modelValue: boolean
  user?: PublicProfileDTO
  ownerMode?: boolean
}>();
const emits = defineEmits<{
  (e: 'update:modelValue', value: boolean): void,
}>();

const { isMobile, isAppleDevice, isAndroidDevice } = useDevices();
const { createApiInstance } = useApi();
const { apps } = useCompanyInfo();
const { account } = useAccountStore();
const { isDesktop } = useUiBreakpoints();
const { showErrorDialog } = useErrorDialog();
const { $addEvent } = useNuxtApp();

const statsLoading = ref(false);
const privilegesLoading = ref(false);
const purchaseCount = ref(0);
const saleCount = ref(0);
const requirements = ref<CommunityInfos[]>([]);
const communityApi = createApiInstance(Community);

const isMale = computed(() => account.value.sex !== 'FEMALE');
const userStatusRequirements = computed<CommunityStatusRequirement>(() => {
  const defaultValue: CommunityStatusRequirement = {
    purchaseRequired: 0,
    saleRequired: 0,
    type: 'OR',
  };

  if (!props.user || !props.user.communityBadge?.status || privilegesLoading.value || props.ownerMode) return defaultValue;

  const statusCode = props.user.communityBadge.status.code!;
  const requirement = requirements.value.find((iterReq) => iterReq.status?.code === statusCode);

  return requirement?.requirements || defaultValue;
});
const statusRequirementsText = computed(() => {
  if (!props.user?.communityBadge?.status || props.ownerMode) return '';

  const { purchaseRequired, saleRequired, type } = userStatusRequirements.value;

  return `Статус <span class="text-status">${props.user.communityBadge?.status.name}</span> дается пользователю за совершение ${purchaseRequired} ${pluralize(purchaseRequired!, [
    'покупки',
    'покупок',
    'покупок',
  ])} ${type === 'AND' ? 'и' : 'или'} ${saleRequired} ${pluralize(saleRequired!, [
    'продажи',
    'продаж',
    'продаж',
  ])}`;
});

function getStatus() {
  statsLoading.value = true;

  communityApi.getStatus({ format: 'json' })
    .then((response) => response.data.data)
    .then((stats) => {
      purchaseCount.value = stats.purchaseCount || 0;
      saleCount.value = stats.saleCount || 0;
    })
    .catch(() => showErrorDialog({
      title: 'Ошибка',
      subtitle: 'Ошибка при отправке запроса',
    }))
    .finally(() => {
      statsLoading.value = false;
    });
}
function getRequirements() {
  privilegesLoading.value = true;

  communityApi.getPrivilegesRequirements({ format: 'json' })
    .then((response) => response.data.data)
    .then((data) => {
      requirements.value = data;
    })
    .catch(() => showErrorDialog({
      title: 'Ошибка',
      subtitle: 'Ошибка при отправке запроса',
    }))
    .finally(() => {
      privilegesLoading.value = false;
    });
}
function onCloseClick() {
  $addEvent(AnalyticsEvents.OCommunityDialogClose, {
    page: props.ownerMode ? 'o!community' : 'o!community_stranger',
  });
}

watch(() => props.modelValue, (isOpened) => {
  if (!isOpened) return;

  $addEvent(AnalyticsEvents.OCommunityDialogView, {
    page: props.ownerMode ? 'o!community' : 'o!community_stranger',
  });

  if (props.ownerMode) {
    getStatus();
  } else {
    getRequirements();
  }
});
</script>

<style lang="scss" scoped>
@import "~/assets/scss/settings/index";

.community-header-image {
  width: 100%;
  height: 100px;
  margin-bottom: 40px;

  &.male {
    background: url('~/assets/img/o-community/watches.png') no-repeat center;
    background-size: auto 100px;
  }
  &.female {
    background: url('~/assets/img/o-community/rings.png') no-repeat center;
    background-size: auto 100px;
  }
}

.wrapper {
  padding-top: 89px;
  padding-bottom: 60px;
  font-family: $font-family-default;

  @include media-query(lg-and-up) {
    width: 504px;
  }
}

.content {
  padding: 0 16px;
  width: 100%;

  @include media-query(lg-and-up) {
    padding: 0 52px;
  }
}

.main-title {
  font-size: 36px;
  font-weight: 500;
  text-align: center;
  margin-top: 0;
}

.main-subtitle {
  font-size: 14px;
  font-weight: 400;
  text-align: center;
  color: $grey-3;
}

.title {
  position: relative;
  font-size: 18px;
  font-weight: 700;
  padding: 10px 0;

  &--top-separated:before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    height: 1px;
    display: block;
    background: $grey;
    opacity: 0.4;
  }
}

.badge {
  max-width: 100%;
  max-width: -moz-available;
  max-width: -webkit-fill-available;

  &--top-margined {
    margin-top: -6px;
  }
}

.nickname {
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
}

.text {
  font-variant-numeric: lining-nums tabular-nums;
  font-size: 14px;
  line-height: 24px;
}

:deep(.text-status) {
  text-decoration: underline;
  font-weight: 600;
}

.counts {
  font-feature-settings: 'clig' off, 'liga' off;
  font-size: 18px;
  font-weight: 600;
}

.counts-duration {
  font-size: 12px;
  font-weight: 400;
  line-height: 20px;
  color: $grey-3;
  margin-top: 4px;
}

.avatar {
  flex-shrink: 0;
  border-width: 0 !important;
}

.non-owner-avatar {
  @apply mr-4;
  @apply text-4xl;

  @include media-query(lg-and-up) {
    margin-left: 16px;
  }
}
</style>
